import {inject, Injectable} from '@angular/core';
import {FirmaEntitiesEffects} from '../../../../../store/firma-entities/firma-entities.effects';
import {DatevWizardGetFirmaActions} from './datev-wizard-get-firma.actions';
import {Actions, concatLatestFrom, createEffect, ofType} from '@ngrx/effects';
import {catchError, concatMap, filter, map, of, switchMap, take} from 'rxjs';
import {mappedHttpErrorResponseOperator, RouterActions, RouterSelectors} from '@adnova/jf-ng-components';
import {Store} from '@ngrx/store';
import {FirmaEntitiesSelectors} from '../../../../../store/firma-entities/firma-entities.selectors';
import {Router} from '@angular/router';
import {DatevIncometaxExportConfigService} from '../../../../../openapi/export-openapi';


@Injectable()
export class DatevWizardGetFirmaEffects {

  private readonly _actions$ = inject(Actions);

  private readonly _firmaEntitiesEffects = inject(FirmaEntitiesEffects);

  private readonly _store = inject(Store);

  private readonly _router = inject(Router);

  private readonly _exportService = inject(DatevIncometaxExportConfigService);

  /**
   * Lädt eine Firma vom Server anhand ihrer ID.
   *
   * @trigger getFirma
   * @dispatch getFirmaSuccess | getFirmaFailure
   * @sideEffect API-Call zu /api/firma/{id}, LocalStorage Update
   */
  readonly getFirma$ = this._firmaEntitiesEffects.getFirma$(
    DatevWizardGetFirmaActions.getFirma,
    DatevWizardGetFirmaActions.getFirmaSuccess,
    DatevWizardGetFirmaActions.getFirmaFailure,
  );

  /**
   * Setzt die Firmen-ID im Store und navigiert zur Case-Selection, wenn aktuell auf get-firma.
   *
   * @trigger getFirmaSuccess
   * @dispatch setFirma, goToCaseSelection (nur wenn auf get-firma Seite)
   */
  readonly getFirmaSuccess$ = createEffect(
    () => this._actions$.pipe(
      ofType(DatevWizardGetFirmaActions.getFirmaSuccess),
      concatLatestFrom(() => this._store.select(RouterSelectors.url)),
      concatMap(([{firmaDto}, currentUrl]) => {
        const actions: any[] = [DatevWizardGetFirmaActions.setFirma({firmenId: firmaDto.id})];

        // Nur navigieren wenn wir tatsächlich auf der get-firma Seite sind
        // Nutzt RouterSelectors.url statt router.url um Race Conditions bei OIDC-Auth zu vermeiden
        if (currentUrl && currentUrl.includes('/income-tax-documents/wizard/get-firma')) {
          actions.push(DatevWizardGetFirmaActions.goToCaseSelection({firmenId: firmaDto.id}));
        }

        return actions;
      }),
    ),
  );

  /**
   * Markiert eine Firmen-ID als ungültig nach fehlgeschlagenem Laden.
   *
   * @trigger getFirmaFailure
   * @dispatch addInvalidFirma
   */
  readonly getFirmaFailure$ = createEffect(
    () => this._actions$.pipe(
      ofType(DatevWizardGetFirmaActions.getFirmaFailure),
      concatMap(({firmenId, error}) => ([
        DatevWizardGetFirmaActions.addInvalidFirma({firmenId}),
      ])),
    ),
  );

  /**
   * Navigiert zur Case-Selection Seite im Wizard (neuer Flow).
   *
   * @trigger goToCaseSelection
   * @dispatch RouterActions.navigateByUrl
   */
  readonly goToCaseSelection$ = createEffect(
    () => this._actions$.pipe(
      ofType(DatevWizardGetFirmaActions.goToCaseSelection),
      concatMap(({firmenId}) => ([
        RouterActions.navigateByUrl({url: `/datev/income-tax-documents/wizard/case-selection/inhaber/${firmenId}`}),
      ])),
    ),
  );

  /**
   * Navigiert zur Client-ID Seite im Wizard.
   *
   * @trigger goToClientId
   * @dispatch RouterActions.navigateByUrl
   */
  readonly goToClientId$ = createEffect(
    () => this._actions$.pipe(
      ofType(DatevWizardGetFirmaActions.goToClientId),
      concatMap(({firmenId}) => ([
        RouterActions.navigateByUrl({url: `/datev/income-tax-documents/wizard/client-id/inhaber/${firmenId}`}),
      ])),
    ),
  );

  /**
   * Stellt sicher, dass die Firma geladen ist beim Setzen einer Firmen-ID.
   *
   * @trigger setFirma
   * @dispatch getFirma (wenn Firma nicht im Store)
   * @note Wichtig für direktes Routing zu Wizard-Seiten
   */
  readonly setFirma$ = createEffect(
    () => this._actions$.pipe(
      ofType(DatevWizardGetFirmaActions.setFirma),
      filter((action): action is ReturnType<typeof DatevWizardGetFirmaActions.setFirma> & {firmenId: string} => !!action.firmenId),
      switchMap(({firmenId}) =>
        this._store.select(FirmaEntitiesSelectors.firmaById(firmenId)).pipe(
          take(1),
          filter(firma => !firma),
          switchMap(() => [
            DatevWizardGetFirmaActions.getFirma({firmenId}),
          ]),
        )
      ),
    ),
  );

  /**
   * Synchronisiert die Firma aus der Route ohne Navigation-Side-Effects.
   * Wird von config-details verwendet beim Laden über exportConfigId.
   *
   * @trigger syncFirmaFromRoute
   * @dispatch getFirma (falls Firma nicht im Store)
   * @description Ähnlich wie setFirma$, aber OHNE automatische Navigation zu case-selection.
   * Verhindert Race Conditions beim Direktaufruf von config-details während OIDC-Auth läuft.
   */
  readonly syncFirmaFromRoute$ = createEffect(
    () => this._actions$.pipe(
      ofType(DatevWizardGetFirmaActions.syncFirmaFromRoute),
      filter((action) => !!action.firmenId),
      switchMap(({firmenId}) =>
        this._store.select(FirmaEntitiesSelectors.firmaById(firmenId)).pipe(
          take(1),
          filter(firma => !firma),
          switchMap(() => [
            DatevWizardGetFirmaActions.getFirma({firmenId}),
          ]),
        )
      ),
    ),
  );

  /**
   * Prüft ob die Firma bereits im Store ist und lädt sie bei Bedarf.
   * Wird vom "Weiter"-Button in der get-firma Komponente verwendet.
   *
   * @trigger continueWithFirma
   * @dispatch getFirma (wenn Firma nicht im Store) oder getFirmaSuccess (wenn bereits im Store)
   */
  readonly continueWithFirma$ = createEffect(
    () => this._actions$.pipe(
      ofType(DatevWizardGetFirmaActions.continueWithFirma),
      concatLatestFrom(({firmenId}) =>
        this._store.select(FirmaEntitiesSelectors.firmaById(firmenId))
      ),
      concatMap(([{firmenId}, firma]) => {
        if (firma) {
          // Firma bereits im Store - direkt setFirma dispatchen für Navigation
          return [DatevWizardGetFirmaActions.getFirmaSuccess({firmaDto: firma})];
        } else {
          // Firma nicht im Store - laden
          return [DatevWizardGetFirmaActions.getFirma({firmenId})];
        }
      }),
    ),
  );

  /**
   * Sucht Firmen per Betriebsnummer.
   *
   * @trigger searchFirmaByBetriebsnummer
   * @dispatch searchFirmaSuccess | searchFirmaFailure
   */
  readonly searchFirmaByBetriebsnummer$ = createEffect(
    () => this._actions$.pipe(
      ofType(DatevWizardGetFirmaActions.searchFirmaByBetriebsnummer),
      switchMap(({betriebsnummer}) =>
        this._exportService.getFirmaByBetriebsnummer(betriebsnummer).pipe(
          map(results => DatevWizardGetFirmaActions.searchFirmaSuccess({results})),
          catchError(error => of(error).pipe(
            mappedHttpErrorResponseOperator(error),
            map(error => DatevWizardGetFirmaActions.searchFirmaFailure({error})),
          )),
        ),
      ),
    ),
  );

  /**
   * Sucht Firmen per Firmennummer.
   *
   * @trigger searchFirmaByFirmennummer
   * @dispatch searchFirmaSuccess | searchFirmaFailure
   */
  readonly searchFirmaByFirmennummer$ = createEffect(
    () => this._actions$.pipe(
      ofType(DatevWizardGetFirmaActions.searchFirmaByFirmennummer),
      switchMap(({firmennummer}) =>
        this._exportService.getFirmaByFirmennummer(firmennummer).pipe(
          map(results => DatevWizardGetFirmaActions.searchFirmaSuccess({results})),
          catchError(error => of(error).pipe(
            mappedHttpErrorResponseOperator(error),
            map(error => DatevWizardGetFirmaActions.searchFirmaFailure({error})),
          )),
        ),
      ),
    ),
  );

  /**
   * Sucht Firmen per Partnernummer.
   *
   * @trigger searchFirmaByPartnernummer
   * @dispatch searchFirmaSuccess | searchFirmaFailure
   */
  readonly searchFirmaByPartnernummer$ = createEffect(
    () => this._actions$.pipe(
      ofType(DatevWizardGetFirmaActions.searchFirmaByPartnernummer),
      switchMap(({partnernummer}) =>
        this._exportService.getFirmaByPartnernummer(partnernummer).pipe(
          map(results => DatevWizardGetFirmaActions.searchFirmaSuccess({results})),
          catchError(error => of(error).pipe(
            mappedHttpErrorResponseOperator(error),
            map(error => DatevWizardGetFirmaActions.searchFirmaFailure({error})),
          )),
        ),
      ),
    ),
  );

  /**
   * Firma aus Suchergebnissen auswählen - dispatcht getFirmaSuccess für Navigation.
   *
   * @trigger selectFirmaFromSearchResults
   * @dispatch getFirmaSuccess
   */
  readonly selectFirmaFromSearchResults$ = createEffect(
    () => this._actions$.pipe(
      ofType(DatevWizardGetFirmaActions.selectFirmaFromSearchResults),
      map(({firma}) => DatevWizardGetFirmaActions.getFirmaSuccess({firmaDto: firma})),
    ),
  );

  /**
   * Bei genau einem Suchergebnis automatisch weiterleiten.
   *
   * @trigger searchFirmaSuccess
   * @dispatch getFirmaSuccess (nur bei genau 1 Ergebnis)
   */
  readonly autoNavigateOnSingleResult$ = createEffect(
    () => this._actions$.pipe(
      ofType(DatevWizardGetFirmaActions.searchFirmaSuccess),
      filter(({results}) => results.length === 1),
      map(({results}) => DatevWizardGetFirmaActions.getFirmaSuccess({firmaDto: results[0]})),
    ),
  );

}
